<template>
  <div class="container-fluid">
    <div class="mb-3">
      <h5 class="mb-0 font-weight-bold">Role Permission</h5>
      <p class="text-secondary text-sm mb-0">
        Role access and permission setting
      </p>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <h6 class="mb-0 font-weight-bold">Role Setting</h6>
            <p class="text-secondary text-xs mb-0">
              Manage user roles and their menu permissions.
            </p>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="p-3">
              <div class="d-flex justify-content-end mb-3 px-2">
                <button
                  class="btn btn-sm btn-outline-secondary mb-0"
                  style="font-size: 0.8rem"
                  @click="openCreateModal"
                >
                  <i class="fas fa-plus me-1"></i> Create Role
                </button>
              </div>
              <div v-if="roles.length === 0" class="text-secondary text-xs px-2">
                No roles
              </div>
              <div v-else class="rp-table">
                <div class="rp-table-header">
                  <span class="col-name">ROLE NAME</span>
                  <span class="col-status text-center">STATUS</span>
                  <span class="col-action text-center">ACTION</span>
                </div>
                <div v-for="r in roles" :key="r.roleId" class="rp-row">
                  <div class="col-name">
                    <p class="rp-name mb-0">{{ r.roleName }}</p>
                  </div>
                  <div class="col-status text-center">
                    <base-select
                      size="sm"
                      class="d-inline-block"
                      style="max-width: 140px"
                      :model-value="r.isActive ? 'active' : 'inactive'"
                      :options="roleStatusOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="(v) => updateRoleStatus(r, v)"
                    />
                  </div>
                  <div class="col-action text-center">
                    <button
                      class="btn btn-link btn-sm p-0 action-edit me-2"
                      title="Edit"
                      @click="openEditModal(r)"
                    >
                      <i class="fas fa-edit" style="font-size: 0.85rem"></i>
                    </button>
                    <button
                      class="btn btn-link btn-sm p-0 action-delete"
                      title="Delete"
                      @click="removeRole(r)"
                    >
                      <i
                        class="fas fa-trash-alt"
                        style="font-size: 0.85rem"
                      ></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="#app">
    <div v-if="showModal" class="role-perm-overlay">
      <div class="role-perm-overlay-dim"></div>
      <div class="role-perm-overlay-panel">
          <div class="modal-header">
            <h6 class="modal-title">
              {{ editingRoleId ? "Edit Role" : "Add Role" }}
            </h6>
          </div>
          <div class="modal-body role-perm-modal-body">
            <div class="mb-3">
              <label class="form-label text-xs"
                >Role Name <span class="text-danger">*</span></label
              >
              <input
                v-model="modalRoleName"
                type="text"
                class="form-control form-control-sm"
                placeholder="Role name"
              />
            </div>
            <div
              v-if="modalRows.length === 0"
              class="text-secondary text-sm py-3"
            >
              No menu setting rows
            </div>
            <div v-else class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th>Menu</th>
                    <th class="text-center">View</th>
                    <th class="text-center">Create</th>
                    <th class="text-center">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in modalDisplayRows"
                    :key="item.row.menuKey || item.row.menuId"
                    :class="{ 'menu-child-row': item.depth > 0 }"
                  >
                    <td :style="menuCellStyle(item.depth)">
                      <div class="d-flex align-items-start gap-2">
                        <span
                          v-if="item.depth > 0"
                          class="submenu-branch text-secondary"
                          aria-hidden="true"
                        >↳</span>
                        <div>
                          <div class="fw-bold text-sm">{{ item.row.menuName }}</div>
                          <div class="text-xs text-secondary">
                            {{ item.row.sectionName }} · {{ item.row.routePath }}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="text-center">
                      <label
                        class="bo-checkbox bo-checkbox--sm"
                        @click.prevent="toggleModalPerm(item.row, 'canView')"
                      >
                        <input
                          class="bo-checkbox-input"
                          type="checkbox"
                          :checked="item.row.canView"
                          tabindex="-1"
                        />
                        <span class="bo-checkbox-box"></span>
                      </label>
                    </td>
                    <td class="text-center">
                      <label
                        class="bo-checkbox bo-checkbox--sm"
                        @click.prevent="toggleModalPerm(item.row, 'canCreate')"
                      >
                        <input
                          class="bo-checkbox-input"
                          type="checkbox"
                          :checked="item.row.canCreate"
                          tabindex="-1"
                        />
                        <span class="bo-checkbox-box"></span>
                      </label>
                    </td>
                    <td class="text-center">
                      <label
                        class="bo-checkbox bo-checkbox--sm"
                        @click.prevent="toggleModalPerm(item.row, 'canEdit')"
                      >
                        <input
                          class="bo-checkbox-input"
                          type="checkbox"
                          :checked="item.row.canEdit"
                          tabindex="-1"
                        />
                        <span class="bo-checkbox-box"></span>
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <hr class="my-4" />
            <h6 class="text-sm font-weight-bold mb-1">Plan Detail — Visible Tabs</h6>
            <p class="text-xs text-secondary mb-3">
              Choose which tabs appear on the Test Plan detail page for this role.
            </p>
            <div
              v-if="modalFeatureRows.length === 0"
              class="text-secondary text-sm py-2"
            >
              No feature settings
            </div>
            <div v-else class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th>Tab</th>
                    <th>Group</th>
                    <th class="text-center">View</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="feat in modalFeatureRows" :key="feat.featureId">
                    <td>
                      <div class="fw-bold text-sm">{{ feat.featureName }}</div>
                      <div class="text-xs text-secondary">{{ feat.featureKey }}</div>
                    </td>
                    <td class="text-sm text-secondary">{{ feat.groupName }}</td>
                    <td class="text-center">
                      <label
                        class="bo-checkbox bo-checkbox--sm"
                        @click.prevent="toggleFeaturePerm(feat)"
                      >
                        <input
                          class="bo-checkbox-input"
                          type="checkbox"
                          :checked="feat.canView"
                          tabindex="-1"
                        />
                        <span class="bo-checkbox-box"></span>
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-secondary" @click="closeModal">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-sm btn-dark"
              :disabled="savingPermission"
              @click="saveRoleWithPermissions"
            >
              Save
            </button>
          </div>
        </div>
    </div>
    </teleport>

    <teleport to="#app">
      <div v-if="showNotice" class="role-perm-overlay rp-dialog-overlay">
        <div class="role-perm-overlay-dim" @click="closeNotice"></div>
        <div class="role-perm-overlay-panel rp-dialog-panel">
          <div class="modal-header">
            <h6 class="modal-title">{{ noticeTitle }}</h6>
          </div>
          <div class="modal-body">
            <p class="text-sm mb-0" style="color:#344767; white-space:pre-wrap;">{{ noticeMessage }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-dark mb-0" @click="closeNotice">OK</button>
          </div>
        </div>
      </div>
    </teleport>

    <teleport to="#app">
      <div v-if="showConfirm" class="role-perm-overlay rp-dialog-overlay">
        <div class="role-perm-overlay-dim" @click="cancelConfirm"></div>
        <div class="role-perm-overlay-panel rp-dialog-panel">
          <div class="modal-header">
            <h6 class="modal-title">{{ confirmTitle }}</h6>
          </div>
          <div class="modal-body">
            <p class="text-sm mb-0" style="color:#344767;">{{ confirmMessage }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-secondary mb-0" @click="cancelConfirm">Cancel</button>
            <button type="button" class="btn btn-sm btn-outline-danger mb-0" @click="acceptConfirm">
              {{ confirmOkLabel }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <div
      v-if="showMenuModal"
      class="modal-backdrop"
      style="
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1040;
      "
         ></div>
    <div
      v-if="showMenuModal"
      class="modal d-block"
      tabindex="-1"
      style="z-index: 1050"
    >
      <div class="modal-dialog modal-md modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">Edit Menu</h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label text-xs"
                >Menu Name <span class="text-danger">*</span></label
              >
              <input
                v-model="menuForm.menuName"
                type="text"
                class="form-control form-control-sm"
                placeholder="Menu name"
              />
            </div>
            <div class="mb-3">
              <label class="form-label text-xs"
                >Section <span class="text-danger">*</span></label
              >
              <input
                v-model="menuForm.sectionName"
                type="text"
                class="form-control form-control-sm"
                placeholder="Section name"
              />
            </div>
            <div>
              <label class="form-label text-xs"
                >Route <span class="text-danger">*</span></label
              >
              <input
                v-model="menuForm.routePath"
                type="text"
                class="form-control form-control-sm"
                placeholder="/route-path"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-sm btn-secondary" @click="closeMenuModal">
              Cancel
            </button>
            <button
              class="btn btn-sm btn-dark"
              :disabled="savingMenu"
              @click="saveMenuEdit"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  menuSettingsApi,
  rolePermissionsApi,
  rolesApi,
  featureSettingsApi,
  roleFeaturePermissionsApi,
} from "@/services/backendApi";
import {
  buildMenuDisplayList,
  mergeMenuPermissions,
  mergeKnownMenuChildren,
  normalizeMenuRow,
  permissionsForSave,
} from "@/utils/menuHierarchy";

export default {
  name: "role-permission",
  data() {
    return {
      activeTab: "role",
      loading: false,
      roles: [],
      rows: [],
      savingRole: false,
      savingPermission: false,
      showModal: false,
      editingRoleId: null,
      modalRoleName: "",
      modalRows: [],
      featureRows: [],
      modalFeatureRows: [],
      showMenuModal: false,
      editingMenuId: null,
      savingMenu: false,
      menuForm: {
        menuName: "",
        sectionName: "",
        routePath: "",
      },
      showNotice: false,
      noticeTitle: "",
      noticeMessage: "",
      showConfirm: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmOkLabel: "Delete",
      confirmResolver: null,
    };
  },
  computed: {
    roleStatusOptions() {
      return [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ];
    },
    modalDisplayRows() {
      return buildMenuDisplayList(this.modalRows, { alreadyMerged: true });
    },
  },
  async created() {
    await this.loadRoles();
    await Promise.all([this.loadMenuRows(), this.loadFeatureRows()]);
  },
  methods: {
    mergeFeaturePermissions(baseRows, existingRows) {
      const byId = new Map(
        (existingRows || []).map((f) => [
          Number(f.featureId ?? f.feature_id ?? 0),
          !!(f.canView ?? f.can_view),
        ]),
      );
      return (baseRows || []).map((f) => ({
        ...f,
        canView: byId.get(f.featureId) ?? false,
      }));
    },
    normalizeFeatureRow(f) {
      return {
        featureId: Number(f.featureId ?? f.feature_id ?? 0),
        featureKey: f.featureKey ?? f.feature_key ?? "",
        featureName: f.featureName ?? f.feature_name ?? "",
        groupName: f.groupName ?? f.group_name ?? "",
        canView: !!(f.canView ?? f.can_view),
      };
    },
    async loadFeatureRows() {
      try {
        const features = await featureSettingsApi.getAll();
        this.featureRows = (features || []).map((f) =>
          this.normalizeFeatureRow({ ...f, canView: false }),
        );
      } catch (e) {
        console.error("loadFeatureRows error:", e);
        this.featureRows = [];
      }
    },
    menuCellStyle(depth) {
      return depth > 0 ? { paddingLeft: `${1 + depth * 1.5}rem` } : null;
    },
    openNotice(title, message) {
      this.noticeTitle = title || "Notice";
      this.noticeMessage = message || "";
      this.showNotice = true;
    },
    closeNotice() {
      this.showNotice = false;
    },
    askConfirm({ title, message, okLabel } = {}) {
      this.confirmTitle = title || "Confirm";
      this.confirmMessage = message || "";
      this.confirmOkLabel = okLabel || "OK";
      this.showConfirm = true;
      return new Promise((resolve) => {
        this.confirmResolver = resolve;
      });
    },
    finishConfirm(ok) {
      const resolve = this.confirmResolver;
      this.confirmResolver = null;
      this.showConfirm = false;
      if (typeof resolve === "function") resolve(!!ok);
    },
    acceptConfirm() {
      this.finishConfirm(true);
    },
    cancelConfirm() {
      this.finishConfirm(false);
    },
    toggleModalPerm(row, field) {
      if (!row || !field) return;
      const key = String(row.menuKey || "").trim().toLowerCase();
      const rowId = Number(row.menuId);
      const target =
        (this.modalRows || []).find((r) => {
          if (key && String(r.menuKey || "").trim().toLowerCase() === key) return true;
          if (rowId > 0 && Number(r.menuId) === rowId) return true;
          return false;
        }) || row;
      target[field] = !target[field];
    },
    toggleFeaturePerm(feat) {
      if (!feat) return;
      feat.canView = !feat.canView;
    },
    resolvePermissionMenuId(p) {
      const direct = Number(p?.menuId);
      if (Number.isFinite(direct) && direct > 0) return direct;
      const key = String(p?.menuKey || "").trim().toLowerCase();
      const found = (this.rows || []).find((r) => {
        if (!(Number(r.menuId) > 0)) return false;
        if (key && String(r.menuKey || "").trim().toLowerCase() === key) return true;
        return false;
      });
      return found ? Number(found.menuId) : null;
    },
    defaultMenuRows() {
      return [
        {
          menuId: 1,
          menuName: "Dashboard",
          sectionName: "Main",
          routePath: "/dashboard",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 2,
          menuName: "My Tasks",
          sectionName: "Main",
          routePath: "/my-tasks",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 31,
          menuName: "Project Management",
          sectionName: "Main",
          routePath: "/project-overview",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 3,
          menuName: "Management",
          sectionName: "Main",
          routePath: "/tables",
          isActive: true,
          parentMenuId: 31,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 4,
          menuName: "Plan Management",
          sectionName: "Main",
          routePath: "/test-plan-team",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 15,
          menuName: "QA Management",
          sectionName: "Main",
          routePath: "/qa-management",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 16,
          menuName: "Test Case",
          sectionName: "Main",
          routePath: "/qa-management/test-case",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 17,
          menuName: "Test Run",
          sectionName: "Main",
          routePath: "/qa-management/test-run",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 18,
          menuName: "Test Report",
          sectionName: "Main",
          routePath: "/qa-management/test-report",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 5,
          menuName: "Profile",
          sectionName: "Account",
          routePath: "/profile",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 6,
          menuName: "User Setting",
          sectionName: "Account",
          routePath: "/admin-setting",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 7,
          menuName: "Role Permission",
          sectionName: "System",
          routePath: "/role-permission",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 8,
          menuName: "Team Management",
          sectionName: "Main",
          routePath: "/management",
          isActive: true,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 9,
          menuName: "System Setting",
          sectionName: "System",
          routePath: "/system-setting",
          isActive: true,
          parentMenuId: null,
          sortOrder: 80,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 10,
          menuName: "Sprint Setting",
          sectionName: "System",
          routePath: "/system-setting/sprint-setting",
          isActive: true,
          parentMenuId: 9,
          sortOrder: 81,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 11,
          menuName: "Menu Setting",
          sectionName: "System",
          routePath: "/system-setting/menu-setting",
          isActive: true,
          parentMenuId: 9,
          sortOrder: 82,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
        {
          menuId: 12,
          menuName: "AI Prompt Setting",
          sectionName: "System",
          routePath: "/system-setting/ai-prompt-setting",
          isActive: true,
          parentMenuId: 9,
          sortOrder: 83,
          canView: false,
          canCreate: false,
          canEdit: false,
        },
      ];
    },
    async loadRoles() {
      try {
        const roleRows = await rolesApi.getAll();
        const normalizedRoles = (roleRows || []).map((r) => ({
          roleId: Number(r.roleId ?? r.role_id ?? 0),
          roleName: r.roleName ?? r.role_name ?? "",
          isActive: !!(r.isActive ?? r.is_active),
        }));
        this.roles = normalizedRoles.sort(
          (a, b) => Number(a.roleId || 0) - Number(b.roleId || 0),
        );
      } catch (e) {
        console.error("loadRoles error:", e);
        this.roles = [];
      }
    },
    async loadMenuRows() {
      this.loading = true;
      try {
        const menus = await menuSettingsApi.getAll();
        const source =
          menus && menus.length ? menus : this.defaultMenuRows();
        this.rows = mergeKnownMenuChildren(
          source.map((m) =>
            normalizeMenuRow({
              ...m,
              canView: false,
              canCreate: false,
              canEdit: false,
            }),
          ),
        );
      } catch (e) {
        console.error("loadMenuRows error:", e);
        this.rows = mergeKnownMenuChildren(
          this.defaultMenuRows().map((m) => normalizeMenuRow(m)),
        );
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.editingRoleId = null;
      this.modalRoleName = "";
      this.modalRows = mergeMenuPermissions(this.rows, []).map((r) => ({
        ...r,
        canView: false,
        canCreate: false,
        canEdit: false,
      }));
      this.modalFeatureRows = this.mergeFeaturePermissions(this.featureRows, []);
      this.showModal = true;
    },
    async openEditModal(role) {
      this.editingRoleId = role.roleId;
      this.modalRoleName = role.roleName;
      this.savingRole = true;
      try {
        const [items, featureItems] = await Promise.all([
          rolePermissionsApi.getByRole(role.roleId, { t: Date.now() }),
          roleFeaturePermissionsApi.getByRole(role.roleId, { t: Date.now() }),
        ]);
        this.modalRows = mergeMenuPermissions(
          (items && items.length ? items : this.rows),
          items || [],
          { injectKnown: true },
        );
        this.modalFeatureRows = this.mergeFeaturePermissions(
          this.featureRows,
          featureItems || [],
        );
        this.showModal = true;
      } finally {
        this.savingRole = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.editingRoleId = null;
      this.modalRoleName = "";
      this.modalRows = [];
      this.modalFeatureRows = [];
    },
    async updateRoleStatus(role, val) {
      const next = val === "active";
      await rolesApi.update(role.roleId, { isActive: next });
      await this.loadRoles();
    },
    async removeRole(role) {
      const ok = await this.askConfirm({
        title: "Delete Role",
        message: `Delete role "${role.roleName}"? This cannot be undone.`,
        okLabel: "Delete",
      });
      if (!ok) return;
      try {
        await rolesApi.delete(role.roleId);
        await this.loadRoles();
      } catch (e) {
        this.openNotice("Delete failed", e.message || "Failed to delete role");
      }
    },
    async toggleMenuSettingStatus(row) {
      const isActive = !row.isActive;
      row.isActive = isActive;
      await menuSettingsApi.update(row.menuId, { isActive });
      const t = this.rows.find((x) => x.menuId === row.menuId);
      if (t) t.isActive = isActive;
    },
    openMenuEditModal(row) {
      this.editingMenuId = row.menuId;
      this.menuForm = {
        menuName: row.menuName || "",
        sectionName: row.sectionName || "",
        routePath: row.routePath || "",
      };
      this.showMenuModal = true;
    },
    closeMenuModal() {
      this.showMenuModal = false;
      this.editingMenuId = null;
      this.menuForm = { menuName: "", sectionName: "", routePath: "" };
    },
    async saveMenuEdit() {
      const menuName = this.menuForm.menuName.trim();
      const sectionName = this.menuForm.sectionName.trim();
      const routePath = this.menuForm.routePath.trim();
      if (!menuName || !sectionName || !routePath) return;
      this.savingMenu = true;
      try {
        const payload = {
          menuName,
          sectionName,
          routePath,
          routeName: routePath,
        };
        const updated = await menuSettingsApi.update(this.editingMenuId, payload);
        const idx = this.rows.findIndex((x) => x.menuId === this.editingMenuId);
        if (idx > -1) {
          this.rows.splice(idx, 1, {
            ...this.rows[idx],
            menuName: updated.menuName ?? menuName,
            sectionName: updated.sectionName ?? sectionName,
            routePath: updated.routePath ?? routePath,
            routeName: updated.routeName ?? routePath,
            isActive: !!(updated.isActive ?? this.rows[idx].isActive),
          });
        }
        this.closeMenuModal();
      } catch (e) {
        this.openNotice("Save failed", e.message || "Failed to save menu");
      } finally {
        this.savingMenu = false;
      }
    },
    async saveRoleWithPermissions() {
      const name = this.modalRoleName.trim();
      if (!name) return;
      this.savingPermission = true;
      try {
        let roleId = this.editingRoleId;
        if (!roleId) {
          const created = await rolesApi.insert(name);
          roleId = created.roleId ?? created.role_id;
        } else if (name !== this.roles.find((r) => r.roleId === roleId)?.roleName) {
          await rolesApi.update(roleId, { roleName: name });
        }
        if (!roleId) throw new Error("Missing role id");
        const permissions = permissionsForSave(this.modalRows).map((p) => ({
          ...p,
          menuId: this.resolvePermissionMenuId(p) || p.menuId,
        }));
        await rolePermissionsApi.saveByRole(roleId, permissions);
        const featurePermissions = this.modalFeatureRows.map((r) => ({
          featureId: r.featureId,
          canView: !!r.canView,
        }));
        await roleFeaturePermissionsApi.saveByRole(roleId, featurePermissions);
        await this.loadRoles();
        this.closeModal();
        this.openNotice("Success", "Permissions saved.");
      } catch (e) {
        console.error("saveRoleWithPermissions error:", e);
        this.openNotice("Save failed", e.message || "Failed to save role");
      } finally {
        this.savingPermission = false;
      }
    },
  },
};
</script>

<style scoped>
.card-header.pb-0 {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}

.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
}

.tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}

.rp-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.rp-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rp-row {
  display: flex;
  align-items: center;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}

.rp-row:last-child {
  border-bottom: none;
}

.rp-row:hover {
  background: #fafbfc;
}

.col-name {
  flex: 5;
  min-width: 0;
}

.col-status {
  flex: 1.5;
  min-width: 140px;
}

.col-action {
  flex: 0.8;
  min-width: 70px;
}

.col-menu {
  flex: 5;
  min-width: 0;
}

.col-menu-status {
  flex: 1.2;
  min-width: 130px;
}

.col-menu-action {
  flex: 0.8;
  min-width: 70px;
}

.rp-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #344767;
}

.menu-toggle {
  position: relative;
  width: 44px;
  height: 24px;
  border: 1px solid #d2d6da;
  border-radius: 999px;
  background: #e9ecef;
  transition: all 0.2s ease;
}

.menu-toggle.active {
  border-color: #5e72e4;
  background: #5e72e4;
}

.menu-toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.menu-toggle.active .menu-toggle-knob {
  transform: translateX(20px);
}

.action-edit {
  color: #4361ee;
}

.action-edit:hover {
  color: #2d4bc8;
}

.action-delete {
  color: #e74c3c;
}

.action-delete:hover {
  color: #c0392b;
}

.menu-child-row {
  background: #fafbfc;
}

.menu-child-row:hover {
  background: #f4f6f8;
}

.submenu-branch {
  font-size: 0.85rem;
  line-height: 1.35rem;
  flex-shrink: 0;
}

</style>

<style>
.role-perm-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  box-sizing: border-box;
}

.role-perm-overlay-dim {
  position: absolute;
  inset: 0;
  background: rgba(52, 71, 103, 0.2);
}

.role-perm-overlay-panel {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: min(1100px, 100%);
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(52, 71, 103, 0.16);
}

.role-perm-overlay-panel > .modal-header,
.role-perm-overlay-panel > .modal-footer {
  flex-shrink: 0;
}

.role-perm-modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.rp-dialog-overlay {
  z-index: 4100;
}

.rp-dialog-panel {
  width: min(420px, 100%);
  max-height: none;
}
</style>
